'use client';

import { c as _c } from "react/compiler-runtime";
import { useGSAP } from '@gsap/react';
import { gsap } from 'gsap';
import { useSearchParams } from 'next/navigation';
import { FC, Suspense, useRef } from 'react';
import { resolveFromPostLabel } from '@/components/BlogPage/AuthorHero/utils';
import SocialLinks from '@/components/BlogPage/SocialLinks';
import { BlogAuthorDto } from '@/components/BlogPage/types';
import Image from '@/components/Image';
import PageBreadcrumbs from '@/components/PageBreadcrumbs';
import { Typography } from '@/components/shared/Typography';
import StrapiRichContent from '@/components/StrapiRichContent';
import { breadcrumbTrails } from '@/lib/breadcrumbs';
import css from './style.module.scss';
interface Props {
  author: BlogAuthorDto;
}
const ENTRANCE_FROM = {
  opacity: 0,
  y: 40
};
const ENTRANCE_TO = {
  opacity: 1,
  y: 0,
  duration: 0.8,
  ease: 'power2.out'
};
const ENTRANCE_STAGGER = 0.25;

/**
 * useSearchParams bails its Suspense boundary out of static rendering, so only
 * the breadcrumbs (the one part that needs `?from`) live behind it — the rest
 * of the hero, H1 included, stays in the server HTML.
 */
const AuthorBreadcrumbs: FC<Props> = t0 => {
  const $ = _c(4);
  const {
    author
  } = t0;
  const breadcrumbsRef = useRef(null);
  const searchParams = useSearchParams();
  const fromPostUrl = searchParams.get("from");
  const fromPost = fromPostUrl ? {
    label: resolveFromPostLabel(author, fromPostUrl),
    href: `/blog/post/${fromPostUrl}`
  } : undefined;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      if (breadcrumbsRef.current) {
        gsap.fromTo(breadcrumbsRef.current, ENTRANCE_FROM, ENTRANCE_TO);
      }
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  useGSAP(t1);
  const T0 = PageBreadcrumbs;
  const t2 = breadcrumbTrails.blogAuthor(author.name, fromPost);
  let t3;
  if ($[1] !== T0 || $[2] !== t2) {
    t3 = <T0 ref={breadcrumbsRef} items={t2} withContainer={false} wrapperClassName={css.breadcrumbsWrap} />;
    $[1] = T0;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
};
const AuthorHero: FC<Props> = t0 => {
  const $ = _c(35);
  const {
    author
  } = t0;
  const wrapperRef = useRef(null);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      if (!wrapperRef.current) {
        return;
      }
      const elements = [wrapperRef.current.querySelector(`.${css.leftSide}`), wrapperRef.current.querySelector(`.${css.rightSide}`)].filter(Boolean);
      gsap.fromTo(elements, ENTRANCE_FROM, {
        ...ENTRANCE_TO,
        stagger: ENTRANCE_STAGGER,
        delay: ENTRANCE_STAGGER
      });
    };
    t2 = {
      scope: wrapperRef
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useGSAP(t1, t2);
  let t3;
  if ($[2] !== author.name) {
    t3 = breadcrumbTrails.blogAuthor(author.name);
    $[2] = author.name;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t3) {
    t4 = <PageBreadcrumbs items={t3} withContainer={false} wrapperClassName={css.breadcrumbsWrap} />;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== author) {
    t5 = <AuthorBreadcrumbs author={author} />;
    $[6] = author;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t4 || $[9] !== t5) {
    t6 = <Suspense fallback={t4}>{t5}</Suspense>;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== author.avatar || $[12] !== author.name) {
    t7 = author.avatar?.url ? <Image src={author.avatar.url} alt={author.name || ""} width={author.avatar.width} height={author.avatar.height} quality={100} className={css.avatar} /> : null;
    $[11] = author.avatar;
    $[12] = author.name;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== author.name) {
    t8 = <Typography variant="h3" tag="h1" className={css.name}>{author.name}</Typography>;
    $[14] = author.name;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== author.role) {
    t9 = <Typography variant="body-l" tag="p" className={css.role}>{author.role}</Typography>;
    $[16] = author.role;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t7 || $[19] !== t8 || $[20] !== t9) {
    t10 = <div className={css.leftSide}>{t7}{t8}{t9}</div>;
    $[18] = t7;
    $[19] = t8;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== author.description) {
    t11 = <div className={css.despription}><StrapiRichContent content={author.description} /></div>;
    $[22] = author.description;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== author.socials) {
    t12 = author.socials.length ? <div className={css.socials}><Typography variant="body-l" tag="span">Connect on:</Typography><SocialLinks socials={author.socials} /></div> : null;
    $[24] = author.socials;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== t11 || $[27] !== t12) {
    t13 = <div className={css.rightSide}>{t11}{t12}</div>;
    $[26] = t11;
    $[27] = t12;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== t10 || $[30] !== t13) {
    t14 = <div className={css.content}>{t10}{t13}</div>;
    $[29] = t10;
    $[30] = t13;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== t14 || $[33] !== t6) {
    t15 = <section ref={wrapperRef} className={css.wrapper}>{t6}{t14}</section>;
    $[32] = t14;
    $[33] = t6;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  return t15;
};
export default AuthorHero;